import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { v4 as uuidv4 } from 'uuid'; 
import axios from 'axios';
import { Cookie, ShieldCheck, Check } from 'lucide-react'; 
import './App.css';

// --- SAYFALAR ---
import HomePage from './pages/HomePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import SplashScreen from './components/SplashScreen';

// --- YENİ EKLENEN/DÜZENLENEN SAYFALAR ---
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ServicesPage from './pages/ServicesPage'; 
import ProductDetailPage from './pages/ProductDetailPage';

import { LanguageProvider } from './context/LanguageContext';

// GÜVENLİ API URL ÜRETİCİ
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://mertt.tr/api' 
  : 'http://localhost:8000/api';

// ==========================================================
// --- SESSİZ ZİYARETÇİ İZLEYİCİ (KVKK UYUMLU & UTM DESTEKLİ) ---
// ==========================================================
function PageTracker({ consent }) {
  const location = useLocation();

  useEffect(() => {
    // 1. KENDİ GİRİŞLERİMİZİ SAYMA: Admin panelindeki hareketler istatistiği bozmasın
    if (location.pathname.startsWith('/mertt-komuta-merkezi-99')) return;

    // 2. ÇEREZ ONAY KONTROLÜ (Kritik KVKK/GDPR Noktası)
    // Eğer kullanıcı henüz onay vermediyse veya "Sadece Zorunlu" dediyse (rejected), takip etme!
    if (consent !== 'accepted') {
      return; 
    }

    // 3. Kullanıcının çerezi var mı kontrol et, yoksa yarat
    let visitorId = localStorage.getItem('mertt_visitor_id');
    if (!visitorId) {
      visitorId = uuidv4();
      localStorage.setItem('mertt_visitor_id', visitorId);
    }

    // 4. GELİŞMİŞ TAKİP: URL'deki kampanya veya arama parametrelerini yakala (?q=yazilim veya ?utm_source=instagram)
    const searchParams = new URLSearchParams(window.location.search);
    const queryData = Object.fromEntries(searchParams.entries());

    // Backend'e kullanıcının girdiği sayfayı ve nereden geldiğini sessizce bildir
    axios.post(`${API_URL}/analytics/track`, {
      visitor_id: visitorId,
      path: location.pathname,
      referrer: document.referrer || "Direkt Giriş",
      query_params: queryData
    }).catch(() => {}); 

  }, [location, consent]);

  return null; 
}

// ==========================================
// --- ŞIK ÇEREZ ONAY PANELİ (COOKIE BANNER) ---
// ==========================================
function CookieConsentBanner({ consent, onAcceptAll, onRejectOptional }) {
  // Eğer daha önce bir seçim yapıldıysa bu paneli gizle
  if (consent) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full z-[100] animate-fade-in-up">
      <div className="bg-slate-900/95 backdrop-blur-md border-t border-cyan-900/50 p-6 md:px-12 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center justify-between gap-6">
        
        {/* Sol Taraf: Açıklama */}
        <div className="flex-1 text-left space-y-2">
          <h3 className="text-white font-bold flex items-center gap-2 text-lg">
            <Cookie className="text-cyan-400" size={24} /> 
            Gizlilik ve Çerez Tercihleri (KVKK)
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            Sitemizin düzgün çalışması için <span className="text-cyan-400 font-semibold">zorunlu çerezler</span> kullanılmaktadır. 
            Size daha iyi bir hizmet sunabilmek ve site içi istatistikleri anonim olarak ölçebilmek için 
            <span className="text-blue-400 font-semibold"> analitik çerezlere (isteğe bağlı)</span> onay vermenizi rica ederiz.
          </p>
        </div>

        {/* Sağ Taraf: Aksiyon Butonları */}
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
          <button 
            onClick={onRejectOptional}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-gray-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 transition"
          >
            <ShieldCheck size={18} /> Sadece Zorunlu
          </button>
          
          <button 
            onClick={onAcceptAll}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 transition shadow-lg shadow-cyan-900/50"
          >
            <Check size={18} /> Tümünü Kabul Et
          </button>
        </div>

      </div>
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  
  // Çerez iznini LocalStorage'dan çek
  const [cookieConsent, setCookieConsent] = useState(localStorage.getItem('mertt_cookie_consent'));

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('mertt_cookie_consent', 'accepted');
    setCookieConsent('accepted');
  };

  const handleRejectOptional = () => {
    localStorage.setItem('mertt_cookie_consent', 'rejected');
    // Eğer önceden yaratılmış bir takip id'si varsa, GDPR/KVKK gereği siliyoruz
    localStorage.removeItem('mertt_visitor_id'); 
    setCookieConsent('rejected');
  };

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          {/* HAYALET İZLEYİCİ (Onay durumunu props olarak alır) */}
          <PageTracker consent={cookieConsent} />

          {/* ÇEREZ ONAY PANELİ */}
          <CookieConsentBanner 
            consent={cookieConsent} 
            onAcceptAll={handleAcceptAll} 
            onRejectOptional={handleRejectOptional} 
          />

          {/* Bildirimler (Toast) için ayar */}
          <Toaster position="top-right" richColors />
          
          <Routes>
            {/* --- ANA SAYFA --- */}
            <Route path="/" element={<HomePage />} />

            {/* --- MENÜ SAYFALARI --- */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* HİZMETLER LİSTESİ */}
            <Route path="/services" element={<ServicesPage />} />

            {/* --- DİNAMİK DETAY SAYFALARI --- */}
            <Route path="/services/:id" element={<ServiceDetailPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />

            {/* --- BLOG SAYFALARI --- */}
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:blogId" element={<BlogDetailPage />} />
            
            {/* --- HAYALET ADMİN PANELİ (GÜVENLİK DUVARI V2.0) --- */}
            <Route path="/mertt-komuta-merkezi-99" element={<AdminLogin />} />
            <Route path="/mertt-komuta-merkezi-99/dashboard" element={<AdminDashboard />} />

            {/* --- HATA YÖNETİMİ (404) --- */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider> 
  );
}

export default App;